// src/shared/ui/DeliveryScheduleFields/DeliveryScheduleFields.jsx
import { useSelector } from 'react-redux';
import { RadioCard, RadioCardGroup } from '../RadioCard';
import { DateField } from '../DateField';
import { TimeField } from '../TimeField';
import { styled } from '../styled';

const ScheduleExpand = styled('div')({
  display: 'grid',
  gridTemplateColumns: '1fr 1fr',
  gap: 12,
  padding: 12,
  marginTop: 10,
  background: 'var(--qhs-bg, #f5f7fa)',
  borderRadius: 8,
});

const Field = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  '& > label': {
    fontSize: 12,
    fontWeight: 600,
    color: 'var(--qhs-text-2)',
  },
});

const ErrorBanner = styled('div')({
  margin: '12px 0 0',
  padding: 12,
  borderRadius: 8,
  background: 'rgba(220, 18, 18, 0.08)',
  border: '1px solid rgba(220, 18, 18, 0.24)',
  color: 'var(--qhs-error, #dc1212)',
  fontSize: 12,
  '& strong': { display: 'block', fontWeight: 700, marginBottom: 4 },
});

/**
 * "When to send" picker — sendNow vs schedule, with collapsible
 * Date+Time fields when scheduling. Pure controlled.
 *
 * `sendNowSubtitle` is consumer-supplied so the same primitive serves
 * both the assessment ("a link to take the assessment") and resource
 * ("a link to view the resource") flows.
 *
 * `errors.sendingDataError` renders the inline ErrorBanner below the
 * options — used for the "date / time required to send" pre-flight
 * messages emitted by the consumer's submit handler.
 */
const DeliveryScheduleFields = ({
  values,
  errors = {},
  sendNowSubtitle,
  onChange,
}) => {
  const { configure } = useSelector(state => state.i18n);

  const sendingOption = values?.sendingOption ?? 'sendNow';
  const isScheduled = sendingOption !== 'sendNow';

  return (
    <>
      <RadioCardGroup columns={2}>
        <RadioCard
          name="sendingOption"
          value="sendNow"
          checked={sendingOption === 'sendNow'}
          onChange={() => onChange('sendingOption', 'sendNow')}
          title={configure.sendNow}
          subtitle={sendNowSubtitle}
        />
        <RadioCard
          name="sendingOption"
          value="schedule"
          checked={isScheduled}
          onChange={() => onChange('sendingOption', 'schedule')}
          title={configure.scheduleForLater}
          subtitle={configure.scheduleForLaterDesc}
        />
      </RadioCardGroup>

      {isScheduled && (
        <ScheduleExpand>
          <Field>
            <label>Date</label>
            <DateField
              fullWidth
              value={values?.scheduledDate || ''}
              onChange={s => onChange('scheduledDate', s)}
              minDate={new Date()}
              placeholder="MM/DD/YYYY"
              error={errors.scheduledDate || ''}
            />
          </Field>
          <Field>
            <label>Time</label>
            <TimeField
              minutes={values?.scheduleDeliverTime}
              onMinutesChange={n => onChange('scheduleDeliverTime', n)}
              error={errors.scheduleDeliverTime || ''}
            />
          </Field>
        </ScheduleExpand>
      )}

      {errors.sendingDataError && (
        <ErrorBanner>
          <strong>Validation error</strong>
          {errors.sendingDataError}
        </ErrorBanner>
      )}
    </>
  );
};

export default DeliveryScheduleFields;
